import axios from "axios";

import _ from "lodash";
import { DateTime } from "luxon";
import { apiAddress } from "../../config/server";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../types/appointments/Appointment";
import { IAvailability } from "../../types/availabilities/Availabilities";
import { ICompetition } from "../../types/competition/Competition";
import { IGameConfiguration } from "../../types/game/GameConfiguration";
import { IPerson } from "../../types/persons/person";
import {
  IPublicGame,
  IPublicProcessedGame,
  IPublicProcessedShift,
  IPublicShift,
} from "../../types/shifts/PublicShift";
import {
  IProcessedGame,
  IProcessedSessionContainer,
  IShift,
  IShiftSession,
  IUpcomingSessionTransport,
} from "../../types/shifts/Shift";
import { IShiftSessionEnrolment } from "../../types/shifts/ShiftEnrolment";
import { ITeam } from "../../types/team/Team";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime } from "../../util/date+helper";
import { ApiBase } from "./baseApi";

const ShiftApiBase = `${apiAddress}/api/shift/`;

export const SHIFT_ROSTER_TICK = 5;

export interface IProcessedShiftSessionTransport {
  processed: IProcessedSessionContainer;
  gameConfigs: IGameConfiguration[];
  persons: IPerson[];
  games: IProcessedGame[];
  venues: IVenue[];
  competitions: ICompetition[];
  teams: ITeam[];
  appointments: IAppointment<IAppointmentDetail>[];
  shifts: IShift[];
};

class ShiftApi extends ApiBase {
  static getSupervisableShifts() {
    return ShiftApi.get(`${ShiftApiBase}supervise`).then((res) => {
      if (res.data && res.data.success) {
        return res.data;
      }
    });
  }

  static getManageableShifts() {
    return axios.get(`${ShiftApiBase}manage`).then((res) => {
      if (res.data && res.data.success) {
        return res.data;
      }
      throw new Error(
        res.data
          ? res.data.message
          : "Unknown error ocurred when getting manageable shifts",
      );
    });
  }

  static createShift(shift) {
    return ShiftApi.post<IShift[]>(`${ShiftApiBase}create`, { shift }).then(
      (res) => res.data.data,
    );
  }

  static updateShift(shift) {
    return ShiftApi.patch<IShift[]>(ShiftApiBase + shift._id, { shift }).then(
      (res) => res.data.data,
    );
  }

  static rosterShiftVenueAdministrator(
    sessionId,
    venueId,
    person: string,
    start: Date,
    finish: Date,
    usePersonalPayrate: boolean,
  ): Promise<{
    data: IShift;
    appointments?: IAppointment<IAppointmentDetail>[]
  }> {
    return ShiftApi.post<IShift>(
      `${ShiftApiBase}${sessionId}/admin/${venueId}`,
      {
        person,
        start: start.toISOString(),
        finish: finish.toISOString(),
        usePersonalPayrate,
      },
    ).then((res) => res.data);
  }

  static removeShiftVenueAdmin(
    sessionId: string,
    venueId: string,
    appointmentId: string,
  ): Promise<{
    data: IShift;
    availabilities?: IAvailability[];
    appointments?: IAppointment<any>[];
  }> {
    return ShiftApi.delete<IShift>(
      `${ShiftApiBase}${sessionId}/admin/${venueId}/${appointmentId}`,
    ).then((res) => res.data);
  }

  static rosterShiftVenueSupervisor(
    sessionId,
    venueId,
    person: string,
    start: Date,
    finish: Date,
  ): Promise<{
    data: IShift;
    availabilities?: IAvailability[];
    appointments?: IAppointment<IAppointmentDetail>[];
  }> {
    return ShiftApi.post<IShift>(
      `${ShiftApiBase}${sessionId}/supervise/${venueId}`,
      {
        person,
        start: start.toISOString(),
        finish: finish.toISOString(),
      },
    ).then((res) => res.data);
  }

  static removeShiftVenueSupervisor(
    sessionId: string,
    venueId: string,
    appointmentId: string,
  ): Promise<{
    data: IShift;
    availabilities?: IAvailability[];
    appointments?: IAppointment<any>[];
  }> {
    return ShiftApi.delete<IShift>(
      `${ShiftApiBase}${sessionId}/supervise/${venueId}/${appointmentId}`,
    ).then((res) => res.data);
  }

  static getShiftUrlBy(shiftId, sessionId) {
    return `${ShiftApiBase}${shiftId}/${sessionId}`;
  }

  static getPublishedShift(shiftToken): Promise<IPublicProcessedShift> {
    return ShiftApi.get<IPublicShift>(`${ShiftApiBase + shiftToken}/published`)
      .then((res) => {
        const shift = res.data.data;

        const tick = SHIFT_ROSTER_TICK;
        const start = toDateTime(shift.start);
        const finish = toDateTime(shift.finish);
        const shiftDuration = finish.diff(start, "minutes").minutes + 45;
        const divisions = shiftDuration / tick;

        const indexByDT = (mom: DateTime) =>
          mom.diff(start, "minutes").minutes / tick;
        const prepareGame = (game: IPublicGame, tz: string) =>
          ({
            teams: game.teams,
            competition: game.competition,
            duration: game.duration,
            date: toDateTime(game.date, tz),
            ticks: Math.floor(Number(game.duration) / tick),
            slot: toDateTime(game.date, tz).toFormat("hh:mm a"),
            officials: game.officials,
            labels: game.labels,
          }) as IPublicProcessedGame;

        const venues = _.keyBy(shift.venues, "name");

        const slots: {
          [venue: string]: {
            [court: string]: string | null | IPublicProcessedGame;
          }[];
        } = {}

        Object.keys(shift.games).forEach(venueName => {
          const times: {
            [court: string]: string | null | IPublicProcessedGame;
          }[] = [];
          for (let i = 0; i < divisions; i++) {
            times.push({});
          }

          Object.keys(shift.games[venueName]).forEach((court) => {
            const tz = venues[venueName]?.tz;
            shift.games[venueName][court].forEach((oldGame) => {
              const game = prepareGame(oldGame, tz);
              const idx = indexByDT(game.date);
              const fidx = idx + game.ticks;
              times[idx][court] = game;
              times[idx].slot = game.date.toFormat("hh:mm a");

              for (let i = idx + 1; i < fidx && i < times.length; i++) {
                // remove from start+1
                times[i][court] = null;
              }
            });
          });

          // shorten times
          while (times.length > 0 && Object.keys(times[0]).length == 0) {
            times.splice(0, 1);
          }
          while (
            times.length > 0 &&
            Object.keys(times[times.length - 1]).length == 0
          ) {
            times.splice(times.length - 1, 1);
          }

          slots[venueName] = times;
        })

        return {
          name: shift.name,
          start,
          finish,
          duration: shiftDuration,
          tickDuration: tick,
          ticks: divisions,
          slots,
          venues,
          supervisors: shift.supervisors,
          cacheLastUpdated: shift.cacheLastUpdated ? toDateTime(shift.cacheLastUpdated) : undefined,
        } as IPublicProcessedShift;
      })
      .catch((er) => {
        throw er;
      });
  }

  static getShiftSession(
    shiftId,
    sessionId,
    gridView?: string,
  ): Promise<IProcessedShiftSessionTransport> {
    return ShiftApi.get<{
      session: IShiftSession;
      enrolments: IShiftSessionEnrolment[];
    }>(this.getShiftUrlBy(shiftId, sessionId)).then((res) => {
      const { data } = res;
      if (!data || !data.success) {
        throw new Error(
          (data && data.error) ||
          `Unknown error when getting shift with id: ${shiftId} for date: ${sessionId}`,
        );
      }
      if (!data.games)
        throw new Error("There were no games collected by this session");

      const originalShift = { ...data.shifts?.[0] } as IShift;
      const shift = data.shifts && data.shifts[0];

      if (!shift) throw new Error("No shift was returned by the server");

      const venueMap = _.keyBy(data.venues, "_id");

      const skippedGames: any[] = [];

      const _gridView = gridView || shift.gridView || "venue";
      const byCompetition = _gridView === "competition";

      let gameMap: any[] | undefined;
      const gameMapByVenue = !byCompetition ? {} : undefined;

      // maybe actually organise some games
      // build a sieve

      const tick = SHIFT_ROSTER_TICK;
      let startDate = toDateTime(data.data.session.start);
      // idk why this happened, but to make sure the shiftIndex is exact
      startDate = startDate.set({
        second: 0,
        millisecond: 0,
        minute: Math.round(startDate.get("minute") / tick) * tick,
      });
      const endDate = toDateTime(data.data.session.finish);
      const shiftDuration = endDate.diff(startDate, "minutes").minutes + 45;
      // const shiftDuration = Date.duration(endDate.diff(startDate)).asMinutes() + 45;
      const divisions = shiftDuration / tick;

      // round to account for occasional off start times
      const indexByDateTime = (mom: DateTime) =>
        Math.round(mom.diff(startDate, "minutes").minutes / tick);

      for (const venueId in venueMap) {
        if (!byCompetition) {
          gameMapByVenue![venueId] = _.range(divisions).map(() => [null, {}]);
        } else {
          gameMap = _.range(divisions).map(() => [null, {}]);
        }
      }

      const games: IProcessedGame[] = Array(data.games.length);

      for (let i = 0; i < data.games.length; i++) {
        const game = data.games[i];
        const date = toDateTime(game.date);
        games[i] = {
          ...game,
          _id: game._id,
          duration: game.duration,
          team1: game.team1,
          team2: game.team2,
          competition: game.competition,
          venue: game.venue,
          court: game.court,
          referees: game.referees,
          dateStr: game.date,
          date,
          slot: date,
          persons: game.persons,
        } as IProcessedGame;
      }

      if (!byCompetition) {
        games.forEach((game) => {
          if (
            !game.team1 ||
            !game.team2 ||
            !(game.venue in venueMap) ||
            !game.court
          ) {
            return skippedGames.push(game);
          }

          // we'll calculate it's position, and then blank out all subequent block from there, \
          // anything null represents no game there
          const ticks = Math.floor(Number(game.duration) / tick);
          const startIndex = indexByDateTime(game.date);
          const finishIndex = startIndex + ticks;
          // set at startindex
          if (!gameMapByVenue![game.venue][startIndex]) {
            // debugger;
          }
          const venueCourtKey = `${game.venue}_${game.court}`;
          gameMapByVenue![game.venue][startIndex][0] = game.slot;
          gameMapByVenue![game.venue][startIndex][1][venueCourtKey] = {
            id: game._id,
            length: ticks,
          };
          for (
            let i = startIndex + 1;
            i < finishIndex && i < gameMapByVenue![game.venue].length;
            i++
          ) {
            // remove from start+1
            gameMapByVenue![game.venue][i][1][venueCourtKey] = null;
          }
        });
      } else {
        games.forEach((game) => {
          if (
            !game.team1 ||
            !game.team2 ||
            !(game.venue in venueMap) ||
            !game.court
          ) {
            return skippedGames.push(game);
          }

          // we'll calculate it's position, and then blank out all subequent block from there, \
          // anything null represents no game there
          const ticks = Math.floor(Number(game.duration) / tick);
          const startIndex = indexByDateTime(game.date);
          const finishIndex = startIndex + ticks;
          // set at startindex
          const venueCourtKey = `${game.venue}_${game.court}`;
          if (!gameMap![startIndex]) {
            // debugger;
          }
          gameMap![startIndex][0] = game.slot;
          gameMap![startIndex][1][venueCourtKey] = {
            id: game._id,
            length: ticks,
          };
          for (
            let i = startIndex + 1;
            i < finishIndex && i < gameMap!.length;
            i++
          ) {
            // remove from start+1
            gameMap![i][1][venueCourtKey] = null;
          }
        });
      }

      // try to filter start and end empty slots
      if (!byCompetition) {
        for (const venueId in venueMap) {
          while (
            gameMapByVenue![venueId].length > 0 &&
            Object.keys(gameMapByVenue![venueId][0][1]).length == 0
          ) {
            gameMapByVenue![venueId].splice(0, 1);
          }
          while (
            gameMapByVenue![venueId].length > 0 &&
            Object.keys(
              gameMapByVenue![venueId][gameMapByVenue![venueId].length - 1][1],
            ).length == 0
          ) {
            gameMapByVenue![venueId].splice(
              gameMapByVenue![venueId].length - 1,
              1,
            );
          }
        }
      } else {
        while (gameMap!.length > 0 && Object.keys(gameMap![0][1]).length == 0) {
          gameMap!.splice(0, 1);
        }
        while (
          gameMap!.length > 0 &&
          Object.keys(gameMap![gameMap!.length - 1][1]).length == 0
        ) {
          gameMap!.splice(gameMap!.length - 1, 1);
        }
      }

      const retVal: IProcessedSessionContainer = {
        key: sessionId,
        session: data.data.session,

        enrolments: data.data.enrolments,

        venueIds: Object.keys(venueMap),
        gameIds: data.games.map((g) => g._id),
        gameMap: gameMap!,
        gameMapByVenue,

        skippedGames: [],
      };

      return {
        processed: retVal,
        games,
        persons: data.persons || [],
        gameConfigs: data.gameConfigs || [],
        teams: data.teams || [],
        competitions: data.competitions || [],
        venues: data.venues || [],
        appointments: data.appointments,
        availabilities: data.availabilities || [],
        shifts: [originalShift],
      };
    });
  }

  static publishShiftSession(
    sessionId,
    sendNotifications,
  ): Promise<IShiftSession> {
    return ShiftApi.get<IShiftSession>(
      `${ShiftApiBase + sessionId
      }/confirm?sendNotifications=${sendNotifications}`,
    ).then((resp) => resp.data.data);
  }

  static getUpcomingShifts(): Promise<IUpcomingSessionTransport[]> {
    return ShiftApi.get<IUpcomingSessionTransport[]>(
      `${ShiftApiBase}upcoming`,
    ).then((resp) => resp.data.data);
  }

  static getMyShiftsInRange(
    start: Date,
    finish: Date,
  ): Promise<{ shifts: IShift[]; venues: IVenue[] }> {
    return ShiftApi.get<IShift[]>(
      `${ShiftApiBase}mine/${start.toISOString()}/${finish.toISOString()}`,
    ).then((resp) => ({
      shifts: _.orderBy(resp.data.data, (r) => r.sessions[0].start),
      venues: resp.data.venues || [],
    }));
  }

  static recalculatePayments(shiftId: string, sessionId: string) {
    return ShiftApi.post<object[]>(
      `${ShiftApiBase}${shiftId}/${sessionId}/recalculate`,
    );
  }

  static confirmRosteredAppointments(shiftId: string, sessionId: string): Promise<{ appointments: IAppointment<IAppointmentDetail>[] }> {
    return ShiftApi.post<{ appointments: IAppointment<IAppointmentDetail>[] }>(
      `${ShiftApiBase}${shiftId}/sessions/${sessionId}/confirm-appointments`
    ).then(res => res.data);
  }
}

export default ShiftApi;
